import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertCircle, ArrowDownToLine, ArrowRightLeft, ArrowUpFromLine, BarChart3, Boxes, CircleAlert, Download,
    IndianRupee, Loader2, Package, PackageX, RefreshCw, Search, ShoppingBag, Sparkles, TrendingUp, Truck,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import styles from './InventoryAnalytics.module.css';
import { useStore } from '../../../context/StoreContext';
import { SMIIFY_API } from '../../../config/api';
import GlobalDateFilter from '../../../components/GlobalDateFilter/GlobalDateFilter';

// =============================================================================
// Inventory analytics: stock value, what came in, what was sold / used / lost,
// profit on products, what to reorder and what is not moving.
// Sales are counted on the day the bill was completed (same as Dashboard).
// =============================================================================
const ANALYTICS_API = `${SMIIFY_API}/inventory/analytics`;

const COLOR = {
    inward: 'var(--h-color-success-500, #009e5b)',
    sold: 'var(--h-color-primary-600, #673de6)',
    used: 'var(--danger--500, #fc5185)',
    loss: 'var(--h-color-neutral-700, #36363b)',
    otherOut: 'var(--h-color-neutral-400, #9d9ea4)',
};
const OUT_SERIES = [
    { key: 'sold', label: 'Sold', color: COLOR.sold },
    { key: 'used', label: 'Salon use', color: COLOR.used },
    { key: 'loss', label: 'Damage / loss', color: COLOR.loss },
    { key: 'otherOut', label: 'Returned / transferred', color: COLOR.otherOut },
];

const REASON_LABEL = {
    PURCHASE: 'Vendor purchase', CUSTOMER_RETURN: 'Customer return', TRANSFER_IN: 'Transfer in', ADJUSTMENT_IN: 'Audit correction (+)', OPENING_STOCK: 'Opening stock',
    POS_SALE_REVERSAL: 'Bill edited (returned)', SALE: 'Sale without bill', POS_SALE: 'Sold on bills', INTERNAL_CONSUMPTION: 'Salon use',
    PROMO_SAMPLE: 'Free sample', DAMAGE_EXPIRED: 'Damaged / expired', VENDOR_RETURN: 'Return to vendor', TRANSFER_OUT: 'Transfer out',
    LOST_THEFT: 'Lost / stolen', ADJUSTMENT_OUT: 'Audit correction (-)',
};

const localYmd = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const thisMonth = () => { const n = new Date(); return { startDate: localYmd(new Date(n.getFullYear(), n.getMonth(), 1)), endDate: localYmd(n), label: 'This Month', value: 'thisMonth' }; };

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 });
const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);
const shortInr = (v) => {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return `₹${Math.round(n)}`;
};
const niceMax = (v) => {
    if (v <= 0) return 10;
    const p = 10 ** Math.floor(Math.log10(v));
    return [1, 2, 2.5, 5, 10].map((m) => m * p).find((m) => m >= v);
};
const fmtDay = (ymd) => { const [y, m, d] = String(ymd).split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); };
const fmtWhen = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—');

/* ============================== charts ============================== */
const ChartFrame = ({ top, labels, children, tooltip, legend, unit = 'qty' }) => {
    const labelEvery = Math.max(1, Math.ceil(labels.length / 8));
    return (
        <div className={styles.chart}>
            <div className={styles.chartArea}>
                <div className={styles.yAxis} aria-hidden="true">
                    {[1, 0.75, 0.5, 0.25, 0].map((f) => <span key={f}>{unit === 'money' ? shortInr(top * f) : num(top * f)}</span>)}
                </div>
                <div className={styles.plot}>
                    {[0, 25, 50, 75].map((g) => <i key={g} className={styles.gridLine} style={{ bottom: `${g}%` }} />)}
                    {children}
                    {tooltip}
                </div>
            </div>
            <div className={styles.xAxis} aria-hidden="true">
                {labels.map((l, i) => <span key={l}>{i % labelEvery === 0 ? fmtDay(l) : ''}</span>)}
            </div>
            <div className={styles.legend}>{legend.map((k) => <span key={k.label}><i style={{ background: k.color }} /> {k.label}</span>)}</div>
        </div>
    );
};

const Tooltip = ({ index, count, title, rows }) => (
    <div className={styles.tooltip} style={{ left: `${((index + 0.5) / count) * 100}%` }} role="status">
        <strong>{title}</strong>
        {rows.map((r) => <span key={r.label}><i style={{ background: r.color }} /> {r.label}<b>{r.value}</b></span>)}
    </div>
);

/** Stock in vs stock out, two bars per day */
const InOutChart = ({ points }) => {
    const [hover, setHover] = useState(null);
    const outOf = (p) => p.sold + p.used + p.loss + p.otherOut;
    const top = niceMax(Math.max(0, ...points.map((p) => Math.max(p.inward, outOf(p)))));
    const h = hover !== null ? points[hover] : null;
    return (
        <ChartFrame
            top={top}
            labels={points.map((p) => p.date)}
            legend={[{ label: 'Stock in', color: COLOR.inward }, { label: 'Stock out', color: COLOR.sold }]}
            tooltip={h && (
                <Tooltip index={hover} count={points.length} title={fmtDay(h.date)} rows={[
                    { label: 'Stock in', color: COLOR.inward, value: num(h.inward) },
                    { label: 'Sold', color: COLOR.sold, value: num(h.sold) },
                    { label: 'Salon use', color: COLOR.used, value: num(h.used) },
                    { label: 'Damage / loss', color: COLOR.loss, value: num(h.loss) },
                ]} />
            )}
        >
            <div className={styles.pairBars} onMouseLeave={() => setHover(null)}>
                {points.map((p, i) => (
                    <button
                        type="button"
                        key={p.date}
                        className={styles.pairCol}
                        onMouseEnter={() => setHover(i)}
                        onFocus={() => setHover(i)}
                        onBlur={() => setHover(null)}
                        aria-label={`${fmtDay(p.date)}: in ${p.inward}, out ${outOf(p)}`}
                    >
                        <span style={{ height: `${pct(p.inward, top)}%`, background: COLOR.inward }} />
                        <span style={{ height: `${pct(outOf(p), top)}%`, background: COLOR.sold }} />
                    </button>
                ))}
            </div>
        </ChartFrame>
    );
};

/** Stacked bars (where the stock went, or revenue) */
const StackChart = ({ points, series, unit = 'qty' }) => {
    const [hover, setHover] = useState(null);
    const totals = points.map((p) => series.reduce((s, k) => s + (p[k.key] || 0), 0));
    const top = niceMax(Math.max(0, ...totals));
    const h = hover !== null ? points[hover] : null;
    const show = (v) => (unit === 'money' ? inr(v) : num(v));
    return (
        <ChartFrame
            top={top}
            unit={unit}
            labels={points.map((p) => p.date)}
            legend={series}
            tooltip={h && <Tooltip index={hover} count={points.length} title={fmtDay(h.date)} rows={series.map((k) => ({ label: k.label, color: k.color, value: show(h[k.key]) }))} />}
        >
            <div className={styles.bars} style={{ gap: points.length > 40 ? 1 : points.length > 20 ? 2 : 6 }} onMouseLeave={() => setHover(null)}>
                {points.map((p, i) => (
                    <button
                        type="button"
                        key={p.date}
                        className={`${styles.barCol} ${hover === i ? styles.barColActive : ''}`}
                        onMouseEnter={() => setHover(i)}
                        onFocus={() => setHover(i)}
                        onBlur={() => setHover(null)}
                        aria-label={`${fmtDay(p.date)}: ${series.map((k) => `${k.label} ${show(p[k.key])}`).join(', ')}`}
                    >
                        <span className={styles.barStack} style={{ height: `${pct(totals[i], top)}%` }}>
                            {series.map((k) => (p[k.key] > 0 ? <span key={k.key} style={{ flexGrow: p[k.key], background: k.color }} /> : null))}
                        </span>
                    </button>
                ))}
            </div>
        </ChartFrame>
    );
};

const Donut = ({ parts, centerLabel, centerValue, unit = 'qty' }) => {
    const total = parts.reduce((s, p) => s + p.value, 0);
    const R = 42;
    const C = 2 * Math.PI * R;
    let offset = 0;
    return (
        <div className={styles.donutWrap}>
            <svg viewBox="0 0 100 100" className={styles.donut} role="img" aria-label={`${centerLabel} ${centerValue}`}>
                <circle cx="50" cy="50" r={R} fill="none" stroke="var(--h-color-neutral-100, #f5f5f6)" strokeWidth="12" />
                {total > 0 && parts.map((p) => {
                    const len = (p.value / total) * C;
                    const el = <circle key={p.label} cx="50" cy="50" r={R} fill="none" stroke={p.color} strokeWidth="12" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform="rotate(-90 50 50)" />;
                    offset += len;
                    return el;
                })}
                <text x="50" y="46" textAnchor="middle" className={styles.donutLabel}>{centerLabel}</text>
                <text x="50" y="60" textAnchor="middle" className={styles.donutValue}>{centerValue}</text>
            </svg>
            <ul className={styles.donutLegend}>
                {parts.map((p) => (
                    <li key={p.label}>
                        <i style={{ background: p.color }} />
                        <span>{p.label}</span>
                        <strong>{unit === 'money' ? inr(p.value) : num(p.value)}</strong>
                        <em>{Math.round(pct(p.value, total))}%</em>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const Kpi = ({ icon: Icon, label, value, sub, tone, onClick }) => {
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag type={onClick ? 'button' : undefined} className={`${styles.kpi} ${tone === 'alert' ? styles.kpiAlert : ''} ${onClick ? styles.kpiClickable : ''}`} onClick={onClick}>
            <span className={styles.kpiIcon}><Icon size={15} /></span>
            <span className={styles.kpiText}><small>{label}</small><strong>{value}</strong>{sub && <em>{sub}</em>}</span>
        </Tag>
    );
};

const StatusChip = ({ status }) => {
    if (status === 'OUT_OF_STOCK') return <span className={styles.chipOut}>Empty</span>;
    if (status === 'LOW_STOCK') return <span className={styles.chipLow}>Low</span>;
    return <span className={styles.chipGood}>Good</span>;
};

/* ============================== page ============================== */
const InventoryAnalytics = ({ dateFilter }) => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const storeId = activeStore?.id || activeStore?._id || localStorage.getItem('smiify_active_store_id');

    const [ownRange, setOwnRange] = useState(thisMonth);
    const range = dateFilter || ownRange; // inside the Analytics page the shared date filter is used

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);
    const [tab, setTab] = useState('overview');
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!storeId || storeId === 'global_hq') { setError('Select a branch to see its inventory analytics.'); setData(null); return undefined; }
        if (!range?.startDate) return undefined;
        const controller = new AbortController();
        (async () => {
            setIsLoading(true);
            setError('');
            try {
                const params = new URLSearchParams({
                    storeId: String(storeId), startDate: range.startDate, endDate: range.endDate,
                    tzOffset: String(new Date().getTimezoneOffset()),
                });
                const res = await fetch(`${ANALYTICS_API}?${params}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId },
                    signal: controller.signal,
                });
                const json = await res.json().catch(() => ({}));
                if (res.status === 404) throw new Error('The server does not have inventory analytics yet. Deploy the new backend files.');
                if (!res.ok || json.success === false) throw new Error(json.message || `Could not load inventory analytics (${res.status}).`);
                setData(json.data);
            } catch (e) {
                if (e.name !== 'AbortError') { setError(e.message === 'Failed to fetch' ? 'Cannot reach the server. Try again in a minute.' : e.message); setData(null); }
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        })();
        return () => controller.abort();
    }, [storeId, range?.startDate, range?.endDate, reloadKey]);

    const t = data?.totals;
    const s = data?.stock;

    const productRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (data?.products || []).filter((r) => !q || [r.name, r.brand, r.sku, r.category].some((v) => String(v || '').toLowerCase().includes(q)));
    }, [data, query]);

    /* ----------------------------- export ----------------------------- */
    const handleExport = () => {
        if (!data) return;
        const wb = XLSX.utils.book_new();
        const add = (rows, name) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: 'No data' }]), name);

        add([{
            From: data.range.startDate, To: data.range.endDate,
            'Products': s.skus, 'Units in stock': s.units, 'Stock value (cost)': s.valueCost, 'Stock value (selling)': s.valueRetail,
            'Stock in (qty)': t.inwardQty, 'Stock in value': t.inwardValue, 'Sold (qty)': t.soldQty, 'Sales (incl. GST)': t.revenue,
            'Sales (excl. GST)': t.netRevenue, 'Cost of goods sold': t.cogs, 'Gross profit': t.grossProfit, 'Margin %': t.margin,
            'Salon use (qty)': t.usedQty, 'Salon use value': t.usedValue, 'Damage / loss (qty)': t.lossQty, 'Damage / loss value': t.lossValue,
            'Low stock items': s.lowCount, 'Empty items': s.outCount,
        }], 'Summary');
        add(data.trend.map((p) => ({ Date: p.date, 'Stock in': p.inward, Sold: p.sold, 'Salon use': p.used, 'Damage / loss': p.loss, 'Returned / transferred': p.otherOut, Sales: p.revenue })), 'Daily');
        add(data.products.map((r) => ({
            Product: r.name, Brand: r.brand, SKU: r.sku, Category: r.category, 'Stock in': r.inward, Sold: r.sold, 'Salon use': r.used,
            'Damage / loss': r.loss, Sales: r.revenue, 'Sales excl. GST': r.netRevenue, Cost: r.cost, Profit: r.profit, 'Margin %': r.margin, 'Stock now': r.stock,
        })), 'Products');
        add(data.reorder.map((r) => ({ Product: r.name, Brand: r.brand, SKU: r.sku, Stock: r.stock, 'Min level': r.minAlert, 'Avg used per day': r.avgDailyOut, 'Days left': r.daysLeft ?? '' })), 'Reorder');
        add(data.deadStock.map((r) => ({ Product: r.name, Brand: r.brand, SKU: r.sku, Stock: r.stock, 'Value at cost': r.valueCost })), 'Not moving');
        add(data.movements.map((m) => ({
            When: fmtWhen(m.createdAt), Product: m.productSnapshot?.name, Type: m.actionType === 'INWARD' ? 'In' : 'Out', Reason: REASON_LABEL[m.reason] || m.reason,
            Qty: m.quantity, 'Stock before': m.previousStock, 'Stock after': m.newStock, Value: m.totalValue, Bill: m.invoiceNo, By: m.performedByName, Note: m.note,
        })), 'Movements');
        XLSX.writeFile(wb, `Inventory_Analytics_${data.range.startDate}_to_${data.range.endDate}.xlsx`);
    };

    /* ============================== render ============================== */
    const toolbar = (
        <div className={styles.toolbar}>
            <div className={styles.tabs} role="tablist" aria-label="Inventory analytics view">
                {[
                    ['overview', 'Overview', BarChart3],
                    ['products', 'Products', Package],
                    ['reorder', 'Reorder', PackageX],
                    ['movements', 'Movements', ArrowRightLeft],
                ].map(([key, label, Icon]) => (
                    <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? styles.tabActive : ''} onClick={() => setTab(key)}>
                        <Icon size={13} /> {label}
                        {key === 'reorder' && data?.reorder?.length > 0 && <em className={styles.tabCount}>{data.reorder.length}</em>}
                    </button>
                ))}
            </div>
            <div className={styles.toolbarRight}>
                {!dateFilter && (
                    <span className={styles.dateWrap}>
                        <GlobalDateFilter startDate={ownRange.startDate} endDate={ownRange.endDate} defaultLabel={ownRange.label} onChange={(r) => setOwnRange(r)} />
                    </span>
                )}
                {isLoading && data && <Loader2 size={14} className={styles.spin} aria-label="Updating" />}
                <button type="button" className={styles.btn} onClick={() => navigate('/inventory/overview')} title="Stock overview"><Boxes size={13} /> Stock</button>
                <button type="button" className={styles.btn} onClick={() => navigate('/inventory/vendors')} title="Vendors"><Truck size={13} /> Vendors</button>
                <button type="button" className={styles.btn} onClick={() => setReloadKey((k) => k + 1)} aria-label="Reload" title="Reload"><RefreshCw size={13} /></button>
                <button type="button" className={styles.btnPrimary} onClick={handleExport} disabled={!data}><Download size={13} /> Export</button>
            </div>
        </div>
    );

    if (isLoading && !data) {
        return <div className={styles.page}>{toolbar}<div className={styles.state}><Loader2 size={22} className={styles.spin} /><strong>Calculating inventory...</strong></div></div>;
    }
    if (error && !data) {
        return (
            <div className={styles.page}>
                {toolbar}
                <div className={styles.state} role="alert">
                    <CircleAlert size={22} className={styles.dangerIcon} />
                    <strong>{error}</strong>
                    <button type="button" className={styles.btn} onClick={() => setReloadKey((k) => k + 1)}><RefreshCw size={13} /> Try again</button>
                </div>
            </div>
        );
    }
    if (!data) return <div className={styles.page}>{toolbar}</div>;

    const outParts = [
        { label: 'Sold', value: t.soldQty, color: COLOR.sold },
        { label: 'Salon use', value: t.usedQty, color: COLOR.used },
        { label: 'Damage / loss', value: t.lossQty, color: COLOR.loss },
        { label: 'Returned / transferred', value: t.otherOutQty, color: COLOR.otherOut },
    ];
    const urgent = data.reorder.filter((r) => r.status === 'OUT_OF_STOCK' || (r.daysLeft !== null && r.daysLeft <= 3));

    return (
        <div className={styles.page}>
            {toolbar}

            {/* ================= OVERVIEW ================= */}
            {tab === 'overview' && (
                <>
                    <div className={styles.kpis}>
                        <Kpi icon={Boxes} label="Stock value (cost)" value={inr(s.valueCost)} sub={`${s.skus} products, ${num(s.units)} units`} />
                        <Kpi icon={ShoppingBag} label="Products sold" value={inr(t.revenue)} sub={`${num(t.soldQty)} units, ${t.bills} bills`} />
                        <Kpi icon={TrendingUp} label="Gross profit" value={inr(t.grossProfit)} sub={`Margin ${t.margin}% (excl. GST)`} />
                        <Kpi icon={ArrowDownToLine} label="Stock in" value={num(t.inwardQty)} sub={`Worth ${inr(t.inwardValue)}`} />
                        <Kpi icon={Sparkles} label="Salon use" value={num(t.usedQty)} sub={`Worth ${inr(t.usedValue)}`} />
                        <Kpi icon={PackageX} tone={t.lossQty > 0 ? 'alert' : undefined} label="Damage / loss" value={num(t.lossQty)} sub={`Worth ${inr(t.lossValue)}`} />
                        <Kpi
                            icon={AlertCircle}
                            tone={s.outCount > 0 ? 'alert' : undefined}
                            label="Low / empty"
                            value={`${s.lowCount} / ${s.outCount}`}
                            sub="Click to see reorder list"
                            onClick={() => setTab('reorder')}
                        />
                        <Kpi icon={IndianRupee} label="Stock value (selling)" value={inr(s.valueRetail)} sub={`Possible profit ${inr(s.potentialProfit)}`} />
                    </div>

                    {urgent.length > 0 && (
                        <button type="button" className={styles.alert} onClick={() => setTab('reorder')}>
                            <AlertCircle size={14} />
                            <span><strong>{urgent.length} product{urgent.length === 1 ? '' : 's'}</strong> {urgent.length === 1 ? 'is' : 'are'} empty or will run out within 3 days: {urgent.slice(0, 3).map((r) => r.name).join(', ')}{urgent.length > 3 ? '...' : ''}</span>
                            <em>See list</em>
                        </button>
                    )}

                    <div className={styles.grid2Wide}>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Daily stock in vs out</h3><small>{fmtDay(data.range.startDate)} to {fmtDay(data.range.endDate)}</small></header>
                            <InOutChart points={data.trend} />
                        </section>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Where the stock went</h3><small>{num(t.outQty)} units out</small></header>
                            <Donut parts={outParts} centerLabel="Units out" centerValue={num(t.outQty)} />
                        </section>
                    </div>

                    <div className={styles.grid2Wide}>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Daily stock out by reason</h3></header>
                            <StackChart points={data.trend} series={OUT_SERIES} />
                        </section>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Daily product sales</h3><small>{inr(t.revenue)}</small></header>
                            <StackChart points={data.trend} series={[{ key: 'revenue', label: 'Sales (incl. GST)', color: COLOR.sold }]} unit="money" />
                        </section>
                    </div>

                    <div className={styles.grid2}>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Top selling products</h3><small>By sales</small></header>
                            {data.topSellers.length === 0 ? <p className={styles.empty}>No products sold in this period.</p> : (
                                <ul className={styles.rankList}>
                                    {data.topSellers.map((r) => (
                                        <li key={r.productId || r.name}>
                                            <span className={styles.rankName}>{r.name}<small>{num(r.sold)} sold, profit {inr(r.profit)}</small></span>
                                            <span className={styles.rankBar}><i style={{ width: `${pct(r.revenue, data.topSellers[0].revenue)}%`, background: COLOR.sold }} /></span>
                                            <strong>{inr(r.revenue)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Most used in salon</h3><small>Internal use</small></header>
                            {data.topUsed.length === 0 ? <p className={styles.empty}>No salon use recorded. Record it in Update & Movements, reason "Salon use".</p> : (
                                <ul className={styles.rankList}>
                                    {data.topUsed.map((r) => (
                                        <li key={r.productId || r.name}>
                                            <span className={styles.rankName}>{r.name}<small>{r.brand}</small></span>
                                            <span className={styles.rankBar}><i style={{ width: `${pct(r.used, data.topUsed[0].used)}%`, background: COLOR.used }} /></span>
                                            <strong>{num(r.used)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>

                    <div className={styles.grid2}>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>By reason</h3><small>All movements</small></header>
                            {data.reasons.length === 0 ? <p className={styles.empty}>No stock movements in this period.</p> : (
                                <div className={styles.tableWrap}>
                                    <table className={styles.table}>
                                        <thead><tr><th>Reason</th><th>Type</th><th className={styles.r}>Qty</th><th className={styles.r}>Value</th><th className={styles.r}>Entries</th></tr></thead>
                                        <tbody>
                                            {data.reasons.map((r) => (
                                                <tr key={r.reason}>
                                                    <td><strong>{REASON_LABEL[r.reason] || r.reason}</strong></td>
                                                    <td>{r.actionType === 'INWARD' ? <span className={styles.chipIn}>In</span> : <span className={styles.chipOutward}>Out</span>}</td>
                                                    <td className={styles.r}>{num(r.qty)}</td>
                                                    <td className={styles.r}>{inr(r.value)}</td>
                                                    <td className={styles.r}>{r.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>By category</h3></header>
                            {data.categories.length === 0 ? <p className={styles.empty}>No products yet.</p> : (
                                <div className={styles.tableWrap}>
                                    <table className={styles.table}>
                                        <thead><tr><th>Category</th><th className={styles.r}>Products</th><th className={styles.r}>Units</th><th className={styles.r}>Stock value</th><th className={styles.r}>Sold</th><th className={styles.r}>Sales</th></tr></thead>
                                        <tbody>
                                            {data.categories.map((c) => (
                                                <tr key={c.category}>
                                                    <td><strong>{c.category}</strong></td>
                                                    <td className={styles.r}>{c.skus}</td>
                                                    <td className={styles.r}>{num(c.units)}</td>
                                                    <td className={styles.r}>{inr(c.valueCost)}</td>
                                                    <td className={styles.r}>{num(c.soldQty)}</td>
                                                    <td className={`${styles.r} ${styles.strong}`}>{inr(c.revenue)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>

                    {data.people.length > 0 && (
                        <section className={styles.card}>
                            <header className={styles.cardHead}><h3>Who recorded stock</h3><small>Manual entries (bills not included)</small></header>
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead><tr><th>Staff</th><th className={styles.r}>Units in</th><th className={styles.r}>Units out</th><th className={styles.r}>Entries</th></tr></thead>
                                    <tbody>
                                        {data.people.map((p) => (
                                            <tr key={p.name}>
                                                <td><strong>{p.name}</strong></td>
                                                <td className={`${styles.r} ${styles.qtyIn}`}>+{num(p.inward)}</td>
                                                <td className={`${styles.r} ${styles.qtyOut}`}>-{num(p.outward)}</td>
                                                <td className={styles.r}>{p.entries}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}
                </>
            )}

            {/* ================= PRODUCTS ================= */}
            {tab === 'products' && (
                <section className={styles.card}>
                    <header className={styles.cardHead}>
                        <h3>Product-wise report</h3>
                        <label className={styles.miniSearch}>
                            <Search size={13} />
                            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search product, brand, SKU..." aria-label="Search products" />
                        </label>
                    </header>
                    <div className={`${styles.tableWrap} ${styles.tableTall}`}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Product</th><th>Category</th>
                                    <th className={styles.r}>In</th><th className={styles.r}>Sold</th><th className={styles.r}>Salon use</th><th className={styles.r}>Loss</th>
                                    <th className={styles.r}>Sales</th><th className={styles.r}>Cost</th><th className={styles.r}>Profit</th><th className={styles.r}>Margin</th><th className={styles.r}>Stock now</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productRows.length === 0 ? (
                                    <tr><td colSpan={11} className={styles.emptyCell}>{data.products.length ? 'No product matches the search.' : 'No product activity in this period.'}</td></tr>
                                ) : productRows.map((r) => (
                                    <tr key={r.productId || r.name}>
                                        <td><span className={styles.person}><span><strong>{r.name}</strong><small>{[r.brand, r.sku].filter(Boolean).join(', ')}</small></span></span></td>
                                        <td>{r.category}</td>
                                        <td className={`${styles.r} ${r.inward ? styles.qtyIn : ''}`}>{r.inward ? `+${num(r.inward)}` : '—'}</td>
                                        <td className={styles.r}>{r.sold ? num(r.sold) : '—'}</td>
                                        <td className={styles.r}>{r.used ? num(r.used) : '—'}</td>
                                        <td className={`${styles.r} ${r.loss ? styles.lossText : ''}`}>{r.loss ? num(r.loss) : '—'}</td>
                                        <td className={`${styles.r} ${styles.strong}`}>{r.revenue ? inr(r.revenue) : '—'}</td>
                                        <td className={styles.r}>{r.cost ? inr(r.cost) : '—'}</td>
                                        <td className={`${styles.r} ${r.profit > 0 ? styles.profit : r.profit < 0 ? styles.lossText : ''}`}>{r.revenue ? inr(r.profit) : '—'}</td>
                                        <td className={styles.r}>{r.revenue ? `${r.margin}%` : '—'}</td>
                                        <td className={styles.r}>{num(r.stock)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className={styles.footNote}>Profit = sales without GST minus purchase cost. Bill discounts are spread over the lines, so totals match the bills.</p>
                </section>
            )}

            {/* ================= REORDER ================= */}
            {tab === 'reorder' && (
                <>
                    <section className={styles.card}>
                        <header className={styles.cardHead}><h3>Reorder list</h3><small>Empty, low, or running out within 7 days</small></header>
                        <div className={`${styles.tableWrap} ${styles.tableTall}`}>
                            <table className={styles.table}>
                                <thead><tr><th>Product</th><th>Status</th><th className={styles.r}>Stock</th><th className={styles.r}>Min level</th><th className={styles.r}>Used per day</th><th className={styles.r}>Days left</th><th className={styles.r}>Last out</th></tr></thead>
                                <tbody>
                                    {data.reorder.length === 0 ? (
                                        <tr><td colSpan={7} className={styles.emptyCell}>Nothing to reorder right now.</td></tr>
                                    ) : data.reorder.map((r) => (
                                        <tr key={r.productId} className={styles.clickRow} onClick={() => navigate(`/inventory/update?product=${r.productId}`)} title="Click to add stock">
                                            <td><span className={styles.person}><span><strong>{r.name}</strong><small>{[r.brand, r.sku].filter(Boolean).join(', ')}</small></span></span></td>
                                            <td><StatusChip status={r.status} /></td>
                                            <td className={`${styles.r} ${styles.strong}`}>{num(r.stock)}</td>
                                            <td className={styles.r}>{num(r.minAlert)}</td>
                                            <td className={styles.r}>{r.avgDailyOut ? num(r.avgDailyOut) : '—'}</td>
                                            <td className={`${styles.r} ${r.daysLeft !== null && r.daysLeft <= 3 ? styles.lossText : ''}`}>{r.daysLeft === null ? '—' : `${r.daysLeft} day${r.daysLeft === 1 ? '' : 's'}`}</td>
                                            <td className={`${styles.r} ${styles.muted}`}>{r.lastOutAt ? fmtWhen(r.lastOutAt) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className={styles.footNote}>Days left = stock now divided by average daily use in the last 30 days. Click a row to add stock.</p>
                    </section>

                    <section className={styles.card}>
                        <header className={styles.cardHead}><h3>Not moving</h3><small>In stock, but nothing went out in 60 days</small></header>
                        {data.deadStock.length === 0 ? <p className={styles.empty}>Every product moved in the last 60 days.</p> : (
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead><tr><th>Product</th><th>Category</th><th className={styles.r}>Stock</th><th className={styles.r}>Money stuck (cost)</th></tr></thead>
                                    <tbody>
                                        {data.deadStock.map((r) => (
                                            <tr key={r.productId}>
                                                <td><span className={styles.person}><span><strong>{r.name}</strong><small>{[r.brand, r.sku].filter(Boolean).join(', ')}</small></span></span></td>
                                                <td>{r.category}</td>
                                                <td className={styles.r}>{num(r.stock)}</td>
                                                <td className={`${styles.r} ${styles.strong}`}>{inr(r.valueCost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </>
            )}

            {/* ================= MOVEMENTS ================= */}
            {tab === 'movements' && (
                <section className={styles.card}>
                    <header className={styles.cardHead}><h3>Stock passbook</h3><small>Latest {data.movements.length} entries in this period</small></header>
                    <div className={`${styles.tableWrap} ${styles.tableTall}`}>
                        <table className={styles.table}>
                            <thead>
                                <tr><th>When</th><th>Product</th><th>Reason</th><th className={styles.r}>Qty</th><th className={styles.r}>Stock</th><th className={styles.r}>Value</th><th>Bill</th><th>By</th><th>Note</th></tr>
                            </thead>
                            <tbody>
                                {data.movements.length === 0 ? (
                                    <tr><td colSpan={9} className={styles.emptyCell}>No stock movements in this period.</td></tr>
                                ) : data.movements.map((m) => (
                                    <tr key={m._id}>
                                        <td className={styles.muted}>{fmtWhen(m.createdAt)}</td>
                                        <td><strong>{m.productSnapshot?.name || 'Product'}</strong></td>
                                        <td>{m.actionType === 'INWARD' ? <ArrowDownToLine size={11} /> : <ArrowUpFromLine size={11} />} {REASON_LABEL[m.reason] || m.reason}</td>
                                        <td className={`${styles.r} ${m.actionType === 'INWARD' ? styles.qtyIn : styles.qtyOut}`}>{m.actionType === 'INWARD' ? '+' : '-'}{m.quantity}</td>
                                        <td className={styles.r}>{m.previousStock} to {m.newStock}</td>
                                        <td className={styles.r}>{m.totalValue ? inr(m.totalValue) : '—'}</td>
                                        <td>{m.invoiceId ? <button type="button" className={styles.link} onClick={() => navigate(`/create-bill/${m.invoiceId}/preview`)}>#{m.invoiceNo}</button> : '—'}</td>
                                        <td>{m.performedByName || '—'}</td>
                                        <td className={styles.muted} style={{ whiteSpace: 'normal', maxWidth: 220 }}>{m.note || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
};

export default InventoryAnalytics;
